import React from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch, useSelector} from 'react-redux';
import {UserService} from '../api';
import {UserProfileUpdate} from '../api/types';
import {
  ForecastPreference,
  ForecastPreferenceField,
  markForecastPreferenceSynced,
  selectForecastPreference,
  selectForecastPreferencePending,
  selectIsLoggedIn,
} from '../store';
import useApiClient from './useApiClient';

// Collapse a rapid run of drag-reorders into a single PATCH.
const FORECAST_PREFERENCE_SYNC_DEBOUNCE_MS = 800;

/**
 * Builds the PATCH body from the pending fields alone, and says which fields
 * the resulting write settles.
 *
 * The backend (`_sync_forecast_preference`) branches on which KEYS the payload
 * names, not on their values:
 *  - `forecast_model_order` alone -> translated into pins/mode.
 *  - any of pins/mode/strategy -> the legacy order is DERIVED from them and
 *    whatever order the payload carried is discarded.
 *
 * So the two are alternative ways of stating the same preference and must
 * never be sent together. When the flat order is pending it wins outright: it
 * is the newer statement of intent, and the server rewrites pins/mode from it,
 * which settles those too — pushing the pre-existing pins afterwards would
 * undo the order the user just dragged.
 */
const buildUpdate = (
  preference: ForecastPreference,
  pending: ForecastPreferenceField[],
): UserProfileUpdate | null => {
  if (pending.includes('order')) {
    return {forecast_model_order: preference.order};
  }
  const update: UserProfileUpdate = {};
  if (pending.includes('pins')) {
    update.forecast_model_pins = preference.pins;
  }
  if (pending.includes('mode')) {
    update.forecast_model_mode = preference.mode;
  }
  if (pending.includes('strategy')) {
    update.forecast_model_strategy = preference.strategy;
  }
  return Object.keys(update).length > 0 ? update : null;
};

const sentSnapshot = (
  preference: ForecastPreference,
  pending: ForecastPreferenceField[],
): Partial<ForecastPreference> => {
  if (pending.includes('order')) {
    return {order: preference.order};
  }
  const snapshot: Partial<ForecastPreference> = {};
  pending.forEach((field) => {
    // TS cannot see that field and preference[field] line up across the union.
    (snapshot as Record<string, unknown>)[field] = preference[field];
  });
  return snapshot;
};

/**
 * Pushes the device-shared forecast model preference (#186: pins, mode,
 * strategy, plus the legacy flat order) up to the backend so phone, tablet and
 * webapp converge. The matching pull is `useUserData.editUserDetails`, which
 * hydrates every field without a pending local edit from every `/me` response
 * — at login, at mount and on each app foreground / window focus.
 *
 * Driven by the pending-field list rather than by diffing against a
 * last-synced baseline, which is what `useDashboardConfigSync` does. Fields
 * arriving from the server are never pending, so a hydrated value cannot be
 * echoed back: there is no "skip the first run" special case to get wrong.
 *
 * The debounce timer is the hook's own, not `useApiClient`'s, so that the
 * effect's cleanup can CANCEL a scheduled write. That matters: a `/me`
 * response landing inside the debounce window settles the pending edit, and
 * `useApiClient`'s debounce — which drops its slot when the timer fires and
 * has no cancel — would still put the abandoned value on the wire and resurrect
 * it on the server.
 *
 * Writes are chained rather than issued concurrently. Two overlapping PATCHes
 * can be applied by the server in either order, and the loser would be brought
 * back by the next hydration; serialising them makes the server's final state
 * the user's last edit.
 *
 * A failed PATCH leaves the fields pending and does not retry on its own — the
 * effect deps have not changed, so it does not re-run and there is no hot
 * loop. The local value stays visible and persisted, hydration will not
 * overwrite it, and the next edit or the next app launch carries it up.
 *
 * Wire this once near the top of the tree (the UserSyncProviders).
 */
const useForecastPreferenceSync = () => {
  const {fetch} = useApiClient();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const preference = useSelector(
    selectForecastPreference,
    isEqual,
  ) as ForecastPreference;
  const pending = useSelector(
    selectForecastPreferencePending,
    isEqual,
  ) as ForecastPreferenceField[];

  // Tail of the chain of writes issued by this hook. Module-scope would be
  // wrong (a second instance is a second component, not a second device) and
  // state would re-render; a ref is exactly the lifetime needed.
  const lastWriteRef = React.useRef<Promise<unknown>>(Promise.resolve());

  React.useEffect(() => {
    // Anonymous edits stay purely local. They keep their pending mark, so the
    // login that follows neither hydrates them away nor needs a merge rule of
    // its own — this effect pushes them once the token lands in Redux.
    if (!isLoggedIn || pending.length === 0) {
      return;
    }

    const update = buildUpdate(preference, pending);
    if (!update) {
      return;
    }
    const snapshot = sentSnapshot(preference, pending);

    const timer = setTimeout(() => {
      lastWriteRef.current = lastWriteRef.current
        .catch(() => {})
        .then(() => fetch(UserService.updateUserProfile(update)))
        .then(() => {
          // The snapshot lets the reducer refuse to settle a field the store
          // has moved on from since this PATCH left.
          dispatch(markForecastPreferenceSynced(snapshot));
        })
        .catch(() => {});
    }, FORECAST_PREFERENCE_SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [preference, pending, isLoggedIn, fetch, dispatch]);
};

export default useForecastPreferenceSync;
